import { useState } from "react";
import styled from "styled-components";
import { get, isEqual, cloneDeep } from "lodash";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { useFormikContext } from "formik";
import { generateId } from "../../../../../../../src/shared/schemas/CharacterSchema";
import { themeVariables } from "src/themeVariables";
import IconButton from "src/components/IconButton";
import Override from "./Override";
import { WORKFLOW_BLOCK_TYPE } from "../../../../../../../src/shared/types/workflows";

// Individual block editors
import { InferenceBlockEditor } from "./InferenceBlockEditor";
import { StoryBibleBlockEditor } from "./StoryBibleBlockEditor";
import ApiInputBlockEditor from "./APIInputBlockEditor";
import ABTestBlockEditor from "./ABTestBlockEditor";
import CostControlsBlockEditor from "./CostControlsBlockEditor";
import { BLOCK_TYPES } from "../../../../../../../src/shared/schemas/PluginSchema";
import CardOutputBlockEditor from "./CardOuputBlockEditor";
import FeatureFlagsBlockEditor from "./FeatureFlagsBlockEditor";
import POVAndTenseBlockEditor from "./POVAndTenseBlockEditor";

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  svg {
    path {
      fill: ${themeVariables.dangerColor};
    }
  }
`;

const ChevronIcon = styled.div`
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${(props) => (props.collapsed ? "rotate(-90deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;

  &:before {
    content: "▼";
    font-size: 12px;
    color: #666;
  }
`;

const OverrideType = {
  SET_OPTIONS: "setOptions",
  SKIP_BLOCK: "skipBlock",
};

const newOverride = () => {
  return {
    id: generateId(),
    name: "Untitled Override",
    type: OverrideType.SET_OPTIONS,
    criteria: [
      {
        id: generateId(),
        options: {
          variable: "",
          value: "",
        },
      },
    ],
    overrides: [],
  };
};

// Block title configuration
const BLOCK_TITLES = {
  inference: (number) => `Prompt ${number}`,
  input_story_bible: () => "Story Bible",
  ab_test: () => "A/B Test",
  api_input: () => "Variables",
  cost_controls: () => "Cost Controls",
  card_output: () => "Card Output",
  input_document: () => "Document Inputs",
  feature_flags: () => "Feature Flags",
  pov_and_tense: () => "POV and Tense",
};

// Block deletion rules
const canDeleteBlock = (block, blockIndex, allBlocks) => {
  if (block.blockType === "api_input") return false;
  if (block.blockType === "card_output") return false;
  if (block.blockType === "inference") {
    const inferenceBlocks = allBlocks.filter((b) => b.blockType === "inference");
    return inferenceBlocks.length > 1;
  }
  return true; // Default: deletable
};

// Block type supports overrides
const blockSupportsOverrides = (blockType) => {
  const blockTypes = [BLOCK_TYPES.INFERENCE, BLOCK_TYPES.COST_CONTROLS];
  return blockTypes.includes(blockType);
};

export const BlockEditor = ({ block, index, workflow }) => {
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [selectedOverrideId, setSelectedOverrideId] = useState(null);
  const [blockSnapshot, setBlockSnapshot] = useState(null);
  const [collapsed, setCollapsed] = useState(true);
  const { values, setFieldValue } = useFormikContext();

  const blockIndex = values.blocks.findIndex((b) => b.id === block.id);
  const optionsPath = `blocks[${blockIndex}].options`;
  const overridesPath = `blocks[${blockIndex}].overrides`;

  const overrides = get(values, overridesPath) || [];

  // Derived state - we're editing if an override is selected
  const isEditingOverride = selectedOverrideId !== null;
  const selectedOverride = overrides.find((override) => override.id === selectedOverrideId);
  const selectedOverrideIndex = selectedOverride ? overrides.findIndex((override) => override.id === selectedOverrideId) : null;

  // Calculate diff between current state and snapshot
  const calculateOptionsDiff = (currentOptions, snapshotOptions) => {
    if (!snapshotOptions) return [];

    const diffs = [];

    // Helper function to recursively compare and build path/value pairs
    const compareObjects = (obj1, obj2, basePath = "") => {
      // Get all keys from both objects to handle additions and modifications
      const allKeys = new Set([...Object.keys(obj1), ...Object.keys(obj2 || {})]);

      allKeys.forEach((key) => {
        const path = basePath ? `${basePath}.${key}` : key;
        const currentValue = obj1[key];
        const snapshotValue = obj2?.[key];

        if (Array.isArray(currentValue) && Array.isArray(snapshotValue)) {
          // Handle arrays by comparing individual elements
          const maxLength = Math.max(currentValue.length, snapshotValue.length);
          for (let i = 0; i < maxLength; i++) {
            const currentItem = currentValue[i];
            const snapshotItem = snapshotValue[i];

            if (currentItem !== undefined && snapshotItem !== undefined) {
              // Both items exist, compare them
              if (typeof currentItem === "object" && currentItem !== null) {
                compareObjects(currentItem, snapshotItem, `${path}.${i}`);
              } else if (!isEqual(currentItem, snapshotItem)) {
                diffs.push({ path: `options.${path}.${i}`, value: currentItem });
              }
            } else if (currentItem !== undefined && snapshotItem === undefined) {
              // New item added to array
              diffs.push({ path: `options.${path}.${i}`, value: currentItem });
            }
          }
        } else if (Array.isArray(currentValue) && !Array.isArray(snapshotValue)) {
          // Current is array but snapshot wasn't - record the whole array
          diffs.push({ path: `options.${path}`, value: currentValue });
        } else if (typeof currentValue === "object" && currentValue !== null && !Array.isArray(currentValue)) {
          // Regular object - recurse into it
          if (typeof snapshotValue === "object" && snapshotValue !== null && !Array.isArray(snapshotValue)) {
            compareObjects(currentValue, snapshotValue, path);
          } else {
            // Snapshot value is not an object, record the whole current value
            diffs.push({ path: `options.${path}`, value: currentValue });
          }
        } else if (!isEqual(currentValue, snapshotValue)) {
          // Primitive value that changed
          diffs.push({ path: `options.${path}`, value: currentValue });
        }
      });
    };

    compareObjects(currentOptions, snapshotOptions);
    return diffs;
  };

  // Process diff and reset block to original state
  const processDiffAndReset = () => {
    if (!isEditingOverride || selectedOverrideIndex === null || !blockSnapshot) return;

    const currentOptions = get(values, `blocks[${blockIndex}].options`);
    const diff = calculateOptionsDiff(currentOptions, blockSnapshot);

    // Update the override with the calculated diff
    const overridePath = `${overridesPath}[${selectedOverrideIndex}]`;
    setFieldValue(`${overridePath}.overrides`, diff);

    // Reset only the options part of the block to snapshot state
    setFieldValue(`blocks[${blockIndex}].options`, blockSnapshot);

    // Clear snapshot and editing state
    setBlockSnapshot(null);
  };

  // Helper function to apply override diffs to current options
  const applyOverrideDiffs = (baseOptions, overrideDiffs) => {
    if (!overrideDiffs || overrideDiffs.length === 0) return baseOptions;

    const modifiedOptions = cloneDeep(baseOptions);

    overrideDiffs.forEach(({ path, value }) => {
      // Remove 'options.' prefix and split into parts
      const cleanPath = path.startsWith("options.") ? path.substring(8) : path;
      const pathParts = cleanPath.split(".");

      // Navigate to the parent object and set the final property
      let current = modifiedOptions;
      for (let i = 0; i < pathParts.length - 1; i++) {
        const part = pathParts[i];
        if (!isNaN(part)) {
          // Handle array indices
          const index = parseInt(part);
          if (!current[index]) current[index] = {};
          current = current[index];
        } else {
          // Handle object properties
          if (!current[part]) current[part] = {};
          current = current[part];
        }
      }

      // Set the final value
      const finalKey = pathParts[pathParts.length - 1];
      if (!isNaN(finalKey)) {
        current[parseInt(finalKey)] = value;
      } else {
        current[finalKey] = value;
      }
    });

    return modifiedOptions;
  };

  // Handle override selection
  const handleOverrideSelected = (overrideId) => {
    if (overrideId !== null) {
      // If we were editing a different override, save that first
      if (isEditingOverride && selectedOverrideId !== overrideId) {
        processDiffAndReset();
      }

      // Start editing mode - snapshot current options only
      if (!isEditingOverride || selectedOverrideId !== overrideId) {
        const currentOptions = get(values, `blocks[${blockIndex}].options`);
        setBlockSnapshot(cloneDeep(currentOptions));

        // Find the override we're about to edit
        const overrideToEdit = overrides.find((override) => override.id === overrideId);

        // Apply existing override diffs to the form so user can see/modify them
        if (overrideToEdit && overrideToEdit.overrides && overrideToEdit.overrides.length > 0) {
          const modifiedOptions = applyOverrideDiffs(currentOptions, overrideToEdit.overrides);
          setFieldValue(`blocks[${blockIndex}].options`, modifiedOptions);
        }

        setSelectedOverrideId(overrideId);
      }
    } else {
      // Stop editing mode - process diff and reset
      if (isEditingOverride) {
        processDiffAndReset();
      }
      setSelectedOverrideId(null);
    }
  };

  // Helper function to get a field value from snapshot using path
  const getSnapshotFieldValue = (fieldPath) => {
    if (!blockSnapshot) return undefined;

    // Remove 'options.' prefix if present since snapshot is options-only
    const cleanPath = fieldPath.startsWith("options.") ? fieldPath.substring(8) : fieldPath;
    const pathParts = cleanPath.split(".");
    let value = blockSnapshot;

    for (const part of pathParts) {
      if (value && typeof value === "object") {
        // Handle array indices like "messages.0.template"
        if (!isNaN(part)) {
          value = value[parseInt(part)];
        } else {
          value = value[part];
        }
      } else {
        return undefined;
      }
    }

    return value;
  };

  // Helper function to get current field value using path
  const getCurrentFieldValue = (fieldPath) => {
    // Convert path to work with current block structure
    const cleanPath = fieldPath.startsWith("options.") ? fieldPath.substring(8) : fieldPath;
    const pathParts = cleanPath.split(".");
    let value = get(values, `blocks[${blockIndex}].options`);

    for (const part of pathParts) {
      if (value && typeof value === "object") {
        // Handle array indices like "messages.0.template"
        if (!isNaN(part)) {
          value = value[parseInt(part)];
        } else {
          value = value[part];
        }
      } else {
        return undefined;
      }
    }

    return value;
  };

  // Check if a field has an override applied (either currently being edited or from existing overrides)
  const hasFieldOverride = (fieldPath) => {
    // If we're actively editing an override, only highlight fields that have actually changed
    if (isEditingOverride && blockSnapshot) {
      const currentValue = getCurrentFieldValue(fieldPath);
      const snapshotValue = getSnapshotFieldValue(fieldPath);
      return !isEqual(currentValue, snapshotValue);
    }

    return false;
  };

  // Generate title for block
  const getBlockTitle = () => {
    const titleGenerator = BLOCK_TITLES[block.blockType];
    if (!titleGenerator) return `${block.blockType} ${index + 1}`;

    if (block.blockType === "inference") {
      const inferenceBlocks = values.blocks.filter((b) => b.blockType === "inference");
      const inferenceIndex = inferenceBlocks.findIndex((b) => b.id === block.id);
      return titleGenerator(inferenceIndex + 1);
    }

    return titleGenerator();
  };

  const supportsOverrides = blockSupportsOverrides(block.blockType);
  const canDelete = canDeleteBlock(block, blockIndex, values.blocks);
  const numOverrides = overrides.length;

  // Render the appropriate child editor
  const renderChildEditor = () => {
    const childProps = {
      key: block.id,
      block,
      index,
      workflow,
      hasFieldOverride: supportsOverrides ? hasFieldOverride : () => false,
      blockIndex,
      optionsPath,
    };

    switch (block.blockType) {
      case WORKFLOW_BLOCK_TYPE.API_INPUT:
        return <ApiInputBlockEditor {...childProps} />;
      case WORKFLOW_BLOCK_TYPE.INPUT_DOCUMENT:
        return (
          <div>
            This provides <pre className="inline-block">previous_document_text</pre> variable as well as set the document chiclets.
          </div>
        );
      case WORKFLOW_BLOCK_TYPE.COST_CONTROLS:
        return <CostControlsBlockEditor {...childProps} />;
      case WORKFLOW_BLOCK_TYPE.INPUT_STORY_BIBLE:
        return <StoryBibleBlockEditor {...childProps} />;
      case WORKFLOW_BLOCK_TYPE.INFERENCE:
        return (
          <InferenceBlockEditor
            {...childProps}
            selectedOverrideId={selectedOverrideId}
            onOverrideSelected={handleOverrideSelected}
            number={index + 1}
          />
        );
      case WORKFLOW_BLOCK_TYPE.AB_TEST:
        return <ABTestBlockEditor {...childProps} />;
      case WORKFLOW_BLOCK_TYPE.CARD_OUTPUT:
        return <CardOutputBlockEditor {...childProps} />;
      case WORKFLOW_BLOCK_TYPE.CLOSE_REQUEST:
        return null;
      case WORKFLOW_BLOCK_TYPE.FEATURE_FLAGS:
        return <FeatureFlagsBlockEditor {...childProps} />;
      case WORKFLOW_BLOCK_TYPE.POV_AND_TENSE:
        return <POVAndTenseBlockEditor {...childProps} />;
      default:
        return null;
    }
  };

  return (
    <div className={`border border-solid p-1 rounded-md ${isEditingOverride ? "border-app-primary-cta border-2 bg-blue-50" : "border-black"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChevronIcon
            collapsed={collapsed}
            onClick={() => setCollapsed(!collapsed)}
          />
          <h3 className="text-lg font-semibold">{getBlockTitle()}</h3>
        </div>
        <div className="flex items-center gap-2">
          {supportsOverrides && (
            <SudoButton
              variant="tertiary"
              className="mt-0 p-0"
              onClick={(e) => {
                e.preventDefault();
                setOverridesOpen(!overridesOpen);
              }}
            >
              Overrides {numOverrides > 0 ? `(${numOverrides})` : ""}
            </SudoButton>
          )}
          {canDelete && (
            <ButtonContainer>
              <IconButton
                className="text-red-500"
                onClick={(e) => {
                  e.preventDefault();
                  const newBlocks = values.blocks.filter((_, i) => i !== blockIndex);
                  setFieldValue("blocks", newBlocks);
                }}
                src="/assets/delete.svg"
              />
            </ButtonContainer>
          )}
        </div>
      </div>

      {!collapsed && (
        <>
          {supportsOverrides && overridesOpen && (
            <div className="flex flex-col gap-2 mb-4">
              {overrides.map((override, index) => (
                <Override
                  key={override.id}
                  override={override}
                  blockIndex={blockIndex}
                  selectedOverrideId={selectedOverrideId}
                  onOverrideSelected={handleOverrideSelected}
                />
              ))}
              <div>
                <SudoButton
                  variant="tertiary"
                  className="mt-0 p-0"
                  onClick={(e) => {
                    e.preventDefault();
                    const newOverrides = [...(get(values, overridesPath) || []), newOverride()];
                    setFieldValue(overridesPath, newOverrides);
                  }}
                >
                  + Override
                </SudoButton>
              </div>
            </div>
          )}

          {renderChildEditor()}
        </>
      )}
    </div>
  );
};

export default BlockEditor;
